import { useState, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CreditCard, Check, Loader2, ShieldCheck } from 'lucide-react';
import API from '@/lib/api';
import { toast } from 'sonner';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

function SetupForm({ onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError('');

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setSaving(false);
      return;
    }

    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message);
      setSaving(false);
      return;
    }

    if (setupIntent && setupIntent.status === 'succeeded') {
      // Set as default payment method
      try {
        await API.post('/billing/agency/payment-methods/default', {
          payment_method_id: setupIntent.payment_method,
        });
      } catch { /* non-critical */ }
      toast.success('Payment method saved successfully');
      if (onSuccess) onSuccess(setupIntent.payment_method);
    } else {
      setError('Setup did not complete. Please try again.');
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="payment-setup-form">
      <div data-testid="payment-element-mount">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>
      {error && <p className="text-xs text-red-500 mt-3" data-testid="setup-error">{error}</p>}
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
        <ShieldCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />
        <p className="text-[10px] text-slate-400 flex-1">Your details are encrypted and sent directly to Stripe. Mylikeness never sees your full card or bank details.</p>
      </div>
      <Button type="submit" disabled={!stripe || saving} data-testid="save-payment-method-btn"
        className="mt-3 w-full bg-slate-900 hover:bg-slate-800 text-white h-10">
        {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : 'Save payment method'}
      </Button>
    </form>
  );
}

export function PaymentSetupButton({ onSuccess, buttonLabel = 'Add payment method', variant = 'outline', className = '' }) {
  const [open, setOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const openSetup = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    setClientSecret(null);
    try {
      const res = await API.post('/billing/agency/setup-intent');
      setClientSecret(res.data.client_secret);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to initialize payment setup');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSuccess = (pmId) => {
    setDone(true);
    setOpen(false);
    if (onSuccess) onSuccess(pmId);
  };

  if (done) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="payment-setup-success">
        <Check className="w-4 h-4 text-emerald-600" />
        <span className="text-sm text-emerald-700 font-medium">Payment method saved</span>
        <Button variant="ghost" size="sm" className="ml-auto text-xs h-7"
          onClick={() => { setDone(false); openSetup(); }}>Add another</Button>
      </div>
    );
  }

  return (
    <>
      <Button onClick={openSetup} disabled={loading} variant={variant} data-testid="add-payment-method-btn"
        className={`rounded-full h-10 ${className}`}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
        {loading ? 'Loading...' : buttonLabel}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <DialogContent className="max-w-md p-0 overflow-hidden gap-0" data-testid="payment-setup-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
            <DialogTitle className="text-base font-heading">Add payment method</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Choose your preferred payment method. Your details are securely handled by Stripe.
            </DialogDescription>
          </DialogHeader>
          <div className="p-5">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            )}
            {!loading && clientSecret && stripePromise && (
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: {
                theme: 'stripe',
                variables: { colorPrimary: '#0f172a', fontFamily: 'system-ui, sans-serif', borderRadius: '8px' },
              }}}>
                <SetupForm onSuccess={handleSuccess} />
              </Elements>
            )}
            {!loading && !clientSecret && (
              <p className="text-sm text-red-500 text-center py-8">Failed to load payment form. Please try again.</p>
            )}
            {!stripePromise && (
              <p className="text-sm text-red-500 text-center py-8">Stripe is not configured.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
